import { AccessRequestStatusTypes } from '@buildwise/libs/company-admin';
import { ContentModal } from '@buildwise/libs/ui';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import CompanyAPI from 'api/CompanyAPI';
import { theme } from 'config/theme';
import { CompanyType, useCompaniesQuery } from 'hooks/useCompanyQueries';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';
import Key2FillIcon from 'remixicon-react/Key2FillIcon';
import useAuthStore from 'store/useAuthStore';
import { catchErrorMessage } from 'utility/helpers';
import { shallow } from 'zustand/shallow';

type RequestWorkspaceAccessModalProps = {
  company: CompanyType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function RequestWorkspaceAccessModal({ company, open, setOpen }: RequestWorkspaceAccessModalProps) {
  const [message, setMessage] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [isRequestedAccess, setRequestedAccess] = useState(false);
  const userAuthDetails = useAuthStore((state) => state.user, shallow);
  const { updateCache } = useCompaniesQuery({
    isAssociatedWithOtherCompanies: !userAuthDetails?.isNovaAdmin,
    shouldFetch: false,
  });

  const handleRequestAccess = useCallback(async () => {
    try {
      setSubmitting(true);
      await CompanyAPI.requestWorkspaceAccess({
        requestedCompanyId: company.id,
        accessType: 'WORKSPACE',
        accessRequestMessage: message.trim(),
      });
      updateCache({
        updatedCompany: {
          ...company,
          accessRequestStatus: AccessRequestStatusTypes.PENDING,
          isAccessRequested: true,
        },
      });
      setRequestedAccess(true);
    } catch (error) {
      console.log('REQUEST_WORKSPACE_ACCESS', error);
      toast.error(catchErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [message, company]);

  return (
    <ContentModal
      open={open}
      onClose={() => setOpen(false)}
      content={
        <>
          {isRequestedAccess ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <CheckboxCircleFillIcon size={60} color={theme.palette.custom.greenBlue} />
              <Typography
                sx={(theme) => ({
                  my: 2,
                  color: theme.palette.custom.tuna,
                  fontSize: 16,
                  fontWeight: 700,
                })}
              >
                Your request has been submitted
              </Typography>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Dismiss
              </Button>
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Key2FillIcon size={36} color={theme.palette.custom.navy} />
                <Typography
                  sx={(theme) => ({
                    mt: 0.5,
                    color: theme.palette.custom.tuna,
                    fontWeight: 700,
                    fontSize: 20,
                    textAlign: 'center',
                  })}
                >
                  Requesting Access to {company.name}
                </Typography>
                <Typography
                  sx={(theme) => ({
                    mt: 1.5,
                    mb: 1,
                    color: theme.palette.custom.mistBlue,
                    fontSize: 14,
                    textAlign: 'center',
                  })}
                >
                  Requesting for{' '}
                  <Typography component="b" fontWeight={700}>
                    {userAuthDetails?.email}
                  </Typography>
                </Typography>
              </Box>

              <TextField
                autoFocus
                multiline
                minRows={5}
                maxRows={10}
                fullWidth
                label={
                  <Typography fontWeight={700} sx={{ display: 'inline' }}>
                    Message
                  </Typography>
                }
                required
                placeholder="Provide the team, project, and/or property you are working on."
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={message}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setMessage(event.target.value);
                }}
                disabled={isSubmitting}
                sx={{ mt: 1 }}
              />
              <Box mt={1} display="flex" alignItems="center" justifyContent="flex-end">
                <Button variant="outlined" onClick={handleRequestAccess} disabled={!message}>
                  {isSubmitting ? (
                    <CircularProgress
                      sx={(theme) => ({ color: theme.palette.custom.black })}
                      size={20}
                    />
                  ) : (
                    'Request Access'
                  )}
                </Button>
              </Box>
            </>
          )}
        </>
      }
    />
  );
}

export default RequestWorkspaceAccessModal;
